import React from "react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Scale, FileText, Users, Handshake } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ArabuluculukSureciHakkinda() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const steps = [{
    id: "item-1",
    title: "1. Başvuru ve Davet",
    icon: <FileText className="w-5 h-5 text-[#C5A059]" />,
    content: "Süreç, taraflardan birinin veya her ikisinin arabulucuya başvurmasıyla başlar. Dava şartı arabuluculukta ise adliye arabuluculuk bürosuna başvuru yapılır. Arabulucu, tarafları en kısa sürede iletişime geçerek ilk toplantıya davet eder ve sürecin başladığını resmen bildirir."
  }, {
    id: "item-2",
    title: "2. Bilgilendirme ve İlk Oturum",
    icon: <Users className="w-5 h-5 text-[#C5A059]" />,
    content: "Taraflar bir araya geldiğinde, arabulucu sürecin temel ilkeleri (gizlilik, gönüllülük, eşitlik) ve hukuki sonuçları hakkında detaylı bilgilendirme yapar. Bu aşamada tarafların sürece güven duyması ve iletişim kanallarının açılması hedeflenir."
  }, {
    id: "item-3",
    title: "3. Müzakere Süreci",
    icon: <Handshake className="w-5 h-5 text-[#C5A059]" />,
    content: "Taraflar, arabulucu eşliğinde uyuşmazlık konularını, ihtiyaçlarını ve menfaatlerini özgürce tartışır. Arabulucu, tarafları yönlendirmez ancak çözüm seçenekleri üretmelerine yardımcı olur. Bu aşama, karşılıklı anlayışın geliştiği ve ortak zeminin arandığı en kritik evredir."
  }, {
    id: "item-4",
    title: "4. Anlaşma ve Son Tutanak",
    icon: <Scale className="w-5 h-5 text-[#C5A059]" />,
    content: "Müzakereler sonucunda taraflar bir anlaşmaya varırsa, bu durum bir 'Anlaşma Belgesi' ile kayıt altına alınır. Bu belge, mahkeme ilamı hükmünde olup icra edilebilir niteliktedir. Anlaşma sağlanamazsa, 'Anlaşamama Tutanağı' düzenlenir ve taraflar dava yoluna gidebilir."
  }];
  return <section id="arabuluculuk-süreci-hakkında" ref={ref} className={`dark bg-linear-to-br from-[#0B1221] via-[#101827] to-[#1A1F2B] text-foreground py-20 lg:py-32 transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {}
          <div className="lg:col-span-5 flex flex-col gap-8 sticky top-24">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A059]/10 border border-[#C5A059]/20 text-[#C5A059] text-xs font-medium tracking-wider uppercase">
                <span className="w-2 h-2 rounded-full bg-[#C5A059] animate-pulse" />
                Süreç Nasıl İşler?
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-medium leading-tight text-[#BCCCDC]">
                Adım Adım <br />
                <span className="text-muted-foreground">Çözüme Ulaşın</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-sans">
                Arabuluculuk, yargılamaya göre daha hızlı, ekonomik ve gizli bir alternatif uyuşmazlık çözüm yoludur. Av. Arb. Muhammed Çelik Hukuk ve Arabuluculuk Bürosu olarak, sürecin her aşamasında tarafsızlığımızı koruyarak profesyonel destek sağlıyoruz.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20">
                <Link to="https://celikhukuk.net/iletisim/">
                  Hemen Başvurun
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent hover:text-accent-foreground">
                <Link to="https://celikhukuk.net/arabuluculuk/">
                  Daha Fazla Bilgi
                </Link>
              </Button>
            </div>

            {}
            <div className="grid grid-cols-2 gap-6 pt-8 border-t border-border/40">
              <div>
                <p className="text-3xl font-serif text-[#BCCCDC]">Hızlı</p>
                <p className="text-sm text-muted-foreground mt-1 font-sans">Mahkemeden daha kısa sürede sonuç</p>
              </div>
              <div>
                <p className="text-3xl font-serif text-[#BCCCDC]">Ekonomik</p>
                <p className="text-sm text-muted-foreground mt-1 font-sans">Daha düşük maliyetli çözüm</p>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 bg-card/30 backdrop-blur-sm border border-border/50 rounded-xl p-6 md:p-8 shadow-2xl shadow-black/20">
            <Accordion type="single" collapsible className="w-full" defaultValue="item-1">
              {steps.map((step, i) => <AccordionItem key={step.id} value={step.id} className="border-border/40 last:border-0 px-2" data-index={i}>
                  <AccordionTrigger className="hover:no-underline py-6 group">
                    <div className="flex items-center gap-4 text-left">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#C5A059]/10 text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-primary-foreground transition-colors duration-300 shrink-0">
                        {step.icon}
                      </div>
                      <span className="text-lg font-medium text-foreground group-hover:text-[#C5A059] transition-colors">
                        {step.title}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pl-[3.5rem] pb-6">
                    {step.content}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

        </div>
      </div>
    </section>;
}